import type { CSSProperties } from 'react';

/** Typography variants — 07-11 semantic type aliases */
export const TYPOGRAPHY_VARIANTS = [
  'heading1',
  'heading2',
  'heading3',
  'body',
  'bodySmall',
  'label',
  'primary',
  'moneyPrimary',
  'moneySecondary',
  'numeric',
  'code',
  'table',
  'caption',
] as const;

export type TypographyVariant = (typeof TYPOGRAPHY_VARIANTS)[number];

/** Maps variant → CSS custom properties (theme-aware via semantic tokens) */
export const TYPOGRAPHY_STYLES: Record<TypographyVariant, CSSProperties> = {
  heading1: {
    fontSize: 'var(--type-heading-1)',
    fontWeight: 'var(--font-weight-bold)',
    lineHeight: 1.25,
    margin: 0,
  },
  heading2: {
    fontSize: 'var(--type-heading-2)',
    fontWeight: 'var(--font-weight-bold)',
    lineHeight: 1.25,
    margin: 0,
  },
  heading3: {
    fontSize: 'var(--type-heading-3)',
    fontWeight: 'var(--font-weight-medium)',
    lineHeight: 1.375,
    margin: 0,
  },
  body: {
    fontSize: 'var(--type-body)',
    fontWeight: 'var(--font-weight-normal)',
    lineHeight: 1.5,
    margin: 0,
  },
  bodySmall: {
    fontSize: 'var(--type-body-small)',
    fontWeight: 'var(--font-weight-normal)',
    lineHeight: 1.5,
    margin: 0,
  },
  label: {
    fontSize: 'var(--type-label)',
    fontWeight: 'var(--font-weight-medium)',
    lineHeight: 1.375,
    margin: 0,
  },
  primary: {
    fontSize: 'var(--type-primary)',
    fontWeight: 'var(--font-weight-medium)',
    lineHeight: 1.25,
    margin: 0,
  },
  moneyPrimary: {
    fontSize: 'var(--type-money-primary)',
    fontWeight: 'var(--font-weight-bold)',
    fontVariantNumeric: 'tabular-nums',
    margin: 0,
  },
  moneySecondary: {
    fontSize: 'var(--type-money-secondary)',
    fontWeight: 'var(--font-weight-medium)',
    fontVariantNumeric: 'tabular-nums',
    margin: 0,
  },
  numeric: {
    fontSize: 'var(--type-numeric)',
    fontVariantNumeric: 'tabular-nums',
    margin: 0,
  },
  code: {
    fontSize: 'var(--type-code)',
    fontFamily: 'ui-monospace, monospace',
    margin: 0,
  },
  table: {
    fontSize: 'var(--type-table, var(--type-body-small))',
    fontWeight: 'var(--font-weight-normal)',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.375,
    margin: 0,
  },
  caption: {
    fontSize: 'var(--type-body-small)',
    color: 'var(--color-text-muted)',
    margin: 0,
  },
};

export function typographyStyle(variant: TypographyVariant): CSSProperties {
  return TYPOGRAPHY_STYLES[variant];
}

/** Tailwind class maps for typography variants */
export const TYPOGRAPHY_CLASSNAMES: Record<TypographyVariant, string> = {
  heading1: 'm-0 text-h1 font-bold leading-tight',
  heading2: 'm-0 text-h2 font-bold leading-tight',
  heading3: 'm-0 text-h3 font-medium leading-snug',
  body: 'm-0 text-body font-normal leading-normal',
  bodySmall: 'm-0 text-body-sm font-normal leading-normal',
  label: 'm-0 text-label font-medium leading-snug',
  primary: 'm-0 text-primary font-medium leading-tight',
  moneyPrimary: 'm-0 text-money font-bold tabular-nums',
  moneySecondary: 'm-0 text-money-secondary font-medium tabular-nums',
  numeric: 'm-0 text-numeric tabular-nums',
  code: 'm-0 font-mono text-code',
  table: 'm-0 text-table font-normal tabular-nums leading-snug',
  caption: 'm-0 text-body-sm text-muted',
};

export function typographyClassName(variant: TypographyVariant): string {
  return TYPOGRAPHY_CLASSNAMES[variant];
}
